import { Box, Button, CircularProgress, Typography } from '@mui/material';
import Alert from '@mui/material/Alert';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { RootState } from '../../app/rootReducer';
import { useCaseLockAccess } from '../../common/caseLocking/CaseLockContext';
import { fetchApplication } from '../detail/detailSlice';
import { DoctorGroup } from '../detail/doctorInfo/groupCandidatesByDoctor';
import {
  ConfirmView,
  DoctorForm,
  DoctorListPanel,
  DoctorsSearch,
} from './components';
import { DoctorFormHandles } from './components/DoctorForm';
import { useApsCandidates } from './hooks';
import styles from './styles.module.scss';
import { Doctor, DoctorFormData } from './types';
import { apsGroupToDoctor, mapDoctorToFormData } from './utils';

type View = 'select' | 'manual' | 'confirm';

const APSOrderDetails = () => {
  const { policyId: applicationId, taskId } = useParams<{
    policyId: string; // This is actually the application ID
    taskId: string;
  }>();
  const [searchParams] = useSearchParams();
  const ehrInsufficient = searchParams.get('EHRInsufficient') === 'true';

  const parsedApplicationId = Number(applicationId);
  const parsedTaskId = Number(taskId);
  const { isLockedForActions, bannerMessage } = useCaseLockAccess();
  const policyId = useSelector(
    (state: RootState) => state.application.termPolicyId,
  );
  const dispatch = useDispatch();
  const formRef = useRef<DoctorFormHandles>(null);

  const [view, setView] = useState<View>('select');
  const [selectedGroup, setSelectedGroup] = useState<DoctorGroup | null>(null);
  const [manualDoctor, setManualDoctor] = useState<Doctor | null>(null);
  const [formData, setFormData] = useState<DoctorFormData | null>(null);
  const [isLoadingApplication, setIsLoadingApplication] = useState(false);
  const [applicationLoadError, setApplicationLoadError] = useState<
    string | null
  >(null);

  const { candidates, isLoading: isLoadingCandidates } = useApsCandidates(
    applicationId ?? '',
    parsedTaskId,
    !!applicationId && !isNaN(parsedTaskId),
  );

  // Fetch application data if policyId is not available
  useEffect(() => {
    if (!policyId && !isNaN(parsedApplicationId) && !isLoadingApplication) {
      setIsLoadingApplication(true);
      setApplicationLoadError(null);

      const fetchApp = fetchApplication(
        applicationId!,
        status => {
          if (status === 'error') {
            setApplicationLoadError('Failed to load application data');
            setIsLoadingApplication(false);
          } else if (status === 'success') {
            setIsLoadingApplication(false);
          }
        },
        [],
        () => {
          // No-op callback
        },
      );

      dispatch(fetchApp as any);
    }
  }, [
    policyId,
    parsedApplicationId,
    applicationId,
    dispatch,
    isLoadingApplication,
  ]);

  const goToSelect = useCallback(() => {
    setView('select');
    setManualDoctor(null);
  }, []);

  const goToManualEmpty = useCallback(() => {
    setManualDoctor(null);
    setView('manual');
  }, []);

  const handleOthersSelect = useCallback((doctor: Doctor | null) => {
    if (!doctor) return;
    setManualDoctor(doctor);
    setView('manual');
  }, []);

  const handleListContinue = useCallback(() => {
    if (!selectedGroup) return;
    const doctor = apsGroupToDoctor(selectedGroup);
    const data = mapDoctorToFormData(doctor);
    setFormData({
      ...data,
      additionalNotes: ehrInsufficient
        ? 'pursue APS only (original EHR retrieved was insufficient)'
        : '',
    });
    setView('confirm');
  }, [selectedGroup, ehrInsufficient]);

  const handleManualContinue = useCallback(async () => {
    const data = await formRef.current?.submit();
    if (!data) return;
    setSelectedGroup(null);
    setFormData(data);
    setView('confirm');
  }, []);

  const backFromConfirm = useCallback(() => {
    setView('select');
  }, []);

  const handleOrderSuccess = useCallback(() => {
    setTimeout(() => {
      window.close();
    }, 1000);
  }, []);

  if (isNaN(parsedApplicationId) || isNaN(parsedTaskId)) {
    return (
      <div className="layout">
        <Alert severity="error">Invalid application ID or task ID</Alert>
      </div>
    );
  }

  if (isLoadingApplication) {
    return (
      <div className="layout">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Loading application data...
          </Typography>
        </Box>
      </div>
    );
  }

  if (applicationLoadError) {
    return (
      <div className="layout">
        <Alert severity="error">{applicationLoadError}</Alert>
      </div>
    );
  }

  if (!policyId) {
    return (
      <div className="layout">
        <Alert severity="error">Application data not available</Alert>
      </div>
    );
  }

  if (isLockedForActions) {
    return (
      <div className="layout">
        <Alert severity="warning">
          {bannerMessage ?? 'This case is locked. You cannot reorder APS evidence.'}
        </Alert>
      </div>
    );
  }

  const panelTitle = 'APS Order Details';
  const panelSubtitle = `Policy Number: ${policyId} · Select a doctor to order APS from`;

  return (
    <div className={styles.Layout}>
      {view === 'select' && (
        <DoctorListPanel
          title={panelTitle}
          subtitle={panelSubtitle}
          candidates={candidates}
          isLoading={isLoadingCandidates}
          selectedGroup={selectedGroup}
          onSelect={setSelectedGroup}
          onContinue={handleListContinue}
          onManualEntry={goToManualEmpty}
          renderOthersPanel={() => (
            <DoctorsSearch
              applicationId={parsedApplicationId}
              taskId={parsedTaskId}
              onSelect={handleOthersSelect}
            />
          )}
        />
      )}

      {view === 'manual' && (
        <div className="manual-panel">
          <div className="panel-header">
            <div className="panel-title">{panelTitle}</div>
            <div className="panel-subtitle">{panelSubtitle}</div>
          </div>
          <div className="back-bar">
            <button type="button" className="back-link" onClick={goToSelect}>
              ← Back to doctor list
            </button>
          </div>
          <div className="form-section">
            <div className="form-title">Doctor / Facility Details</div>
            <DoctorForm
              ref={formRef}
              doctor={manualDoctor}
              insufficientEhr={ehrInsufficient}
            />
          </div>
          <div className="form-actions">
            <Button
              variant="outlined"
              onClick={goToSelect}
              sx={{
                bgcolor: '#fff',
                color: '#374151',
                borderColor: '#e5e7eb',
                lineHeight: '16px',
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleManualContinue}
              sx={{
                bgcolor: '#111827',
                color: '#fff',
                fontSize: 12,
                fontWeight: 600,
                lineHeight: '16px',
              }}
            >
              Continue →
            </Button>
          </div>
        </div>
      )}

      {view === 'confirm' && !!formData && (
        <ConfirmView
          title={panelTitle}
          subtitle={panelSubtitle}
          applicationId={parsedApplicationId}
          taskId={parsedTaskId}
          formData={formData}
          group={selectedGroup}
          onBack={backFromConfirm}
          onCancel={backFromConfirm}
          onSuccess={handleOrderSuccess}
        />
      )}
    </div>
  );
};

export default APSOrderDetails;
